import { useState } from 'react';
import { getResponsiveTextClasses, getResponsiveSpacingClasses, getFocusClasses, getTouchAccessibilityClasses } from '@/lib/responsive-utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import type { CertificationLogo } from '../../types';

interface CertificationsProps {
  certifications: CertificationLogo[];
  className?: string;
}

export function Certifications({ certifications, className = '' }: CertificationsProps) {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [selectedCertification, setSelectedCertification] = useState<CertificationLogo | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleImageLoad = (id: string) => {
    setLoadedImages(prev => new Set(prev).add(id));
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.style.display = 'none';
  };

  const handleCertificationClick = (certification: CertificationLogo) => {
    if (certification.pdfUrl) {
      setSelectedCertification(certification);
      setIsDialogOpen(true);
    } else if (certification.link) {
      window.open(certification.link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => setSelectedCertification(null), 300);
  };

  return (
    <>
      <section className={`${getResponsiveSpacingClasses('py', 8, 10, 12)}  ${getTouchAccessibilityClasses()} ${className}`}>
        <div className="w-full  mx-auto  sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className={`text-center ${getResponsiveSpacingClasses('mb', 8, 10,12)}`}>
            <p className="text-liec-primary-500 text-sm font-semibold mb-2 uppercase tracking-wide">
              Calidad Avalada
              <svg className='inline-flex items-center gap-2 ml-2' width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="0.330774" y1="5.41623" x2="11.9289" y2="11.1156" stroke="#E34C0E" stroke-width="1.5"/>
              <path d="M6.36253 13.292L12.4163 11.2088L10.5026 4.91892" stroke="#E34C0E" stroke-width="1.5"/>
              </svg>
            </p>
             
            <h2 className={getResponsiveTextClasses(
              'text-2xl font-bold text-liec-neutral-900 mb-4',
              'text-3xl',
              'text-4xl'
            )}>
              Nuestras Acreditaciones & Afiliaciones
            </h2>
          </div>

          {/* Certifications Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-y-2 gap-x-4 md:gap-8 justify-items-center">
            {certifications.map((certification) => (
              <button
                key={certification.id}
                onClick={() => handleCertificationClick(certification)}
             
                className={`group relative  p-4 md:p-6 h-32 md:h-48 w-full max-w-[200px] hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${getFocusClasses()} cursor-pointer`}
                
                aria-label={`Ver certificación de ${certification.name}`}
                
              >
                {/* Logo Container */}
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src={certification.logo}
                    alt={certification.alt}
                    className={`max-w-full max-h-full object-contain transition-all duration-300 group-hover:scale-105 ${
                      loadedImages.has(certification.id) ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => handleImageLoad(certification.id)}
                    onError={handleImageError}
                    loading="lazy"
                  />
                  
                  {/* Loading placeholder */}
                  {!loadedImages.has(certification.id) && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-liec-neutral-200 animate-pulse" />
                    </div>
                  )}
                </div>

                {/* Hover overlay with name 
                <div className="absolute inset-0 bg-liec-primary-500 bg-opacity-95 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                  <p className="text-white text-xs md:text-sm font-medium text-center leading-tight">
                    {certification.name}
                    {certification.pdfUrl && (
                      <span className="block mt-2 text-xs opacity-90">Click para ver certificado</span>
                    )}
                  </p>
                </div> */}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PDF Viewer Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{selectedCertification?.name}</DialogTitle>
            <DialogClose onClose={handleCloseDialog} />
          </DialogHeader>
          
          {selectedCertification?.pdfUrl && (
            <div className="w-full h-[70vh] overflow-auto">
              <iframe
                src={selectedCertification.pdfUrl}
                className="w-full h-full border-0"
                title={`Certificación ${selectedCertification.name}`}
              />
            </div>
          )}
          
          <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleCloseDialog}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
            >
              Cerrar
            </button>
            {selectedCertification?.pdfUrl && (
              <a
                href={selectedCertification.pdfUrl}
                download
                className="px-4 py-2 bg-liec-primary-500 text-white rounded-lg hover:bg-liec-primary-600 font-medium transition-colors"
              >
                Descargar PDF
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
